/**
 * Chat / GraphRAG API client.
 *
 * The backend exposes retrieval-only endpoints (LLM stays on the frontend).
 *   1. retrieveContext()  → vector search + 1-hop graph expansion
 *   2. backfillEmbeddings() → re-embed all nodes of an ontology
 *   3. health()           → check that watsonx is configured
 */

import { apiClient } from './client';
import type { GraphProposal } from '@snxt/snxt-onto-share';

export interface RetrievedNode {
  id: string;
  nodeId: string;
  label: string;
  definition: string | null;
  properties: any[];
  similarity: number;
}

export interface RetrievedNeighbor {
  id: string;
  nodeId: string;
  label: string;
  definition: string | null;
  properties: any[];
}

export interface RetrievedEdge {
  edgeId: string;
  source: string;
  target: string;
  label: string | null;
  relationshipType: string | null;
}

export interface GraphRagContext {
  ontologyId: string;
  query: string;
  seeds: RetrievedNode[];
  neighbors: RetrievedNeighbor[];
  edges: RetrievedEdge[];
  promptContext: string;
  totalNodes: number;
  /** Number of nodes still without an embedding (retrieval is degraded if > 0). */
  embeddingsMissing: number;
}

export interface ChatHealth {
  configured: boolean;
  reason?: string;
  endpoint?: string;
  projectId?: string;
}

export interface IndexStatus {
  ontologyId: string;
  /** Total nodes currently persisted for the ontology. */
  totalNodes: number;
  /** Nodes still without an embedding (vector search misses them). */
  embeddingsMissing: number;
}

// ── Co-Ontologist propose ────────────────────────────────────────────────────

export interface ProposeRequest {
  ontologyId: string;
  /** The user's latest message. */
  message: string;
  /** Prior conversation turns (system + user + assistant) for multi-turn context. */
  messages?: Array<{ role: 'system' | 'user' | 'assistant'; content: string }>;
  /** Graph context summary from /chat/retrieve, prepended to the system prompt. */
  graphContext?: string;
  /** Summary of proposals accepted in recent turns, to prevent the LLM from re-proposing them. */
  acceptedContext?: string;
}

export interface ProposeResponse {
  /** The model's text reply. May be empty when the model only emits tool calls. */
  text: string;
  /** Structured mutation proposals. Empty when the model emits no tool calls. */
  proposals: GraphProposal[];
}

export interface ChatCompletionRequest {
  messages: Array<{ role: 'system' | 'user' | 'assistant'; content: string }>;
  model?: string;
  temperature?: number;
  max_tokens?: number;
}

export interface ChatCompletionResponse {
  id: string;
  content: string;
  model: string;
  usage: {
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
  };
  finish_reason: string;
}

// ── Chat History — conversation persistence ────────────────────────────────

export interface StoredMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string; // ISO-8601
  citations?: RetrievedNode[];
  totalNodes?: number;
  embeddingsMissing?: number;
  nodeLookup?: unknown;
  /** Graph mutation proposals proposed by the Co-Ontologist for this message. */
  proposals?: import('@snxt/snxt-onto-share').GraphProposal[];
  /** Per-proposal decisions (accepted/rejected), keyed by proposal index. */
  proposalDecisions?: Record<number, 'accepted' | 'rejected'>;
}

export interface ChatConversationSummary {
  id: string;
  userId: string;
  ontologyId: string;
  title: string;
  isArchived: boolean;
  lastMessageAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ChatConversation extends ChatConversationSummary {
  messages: StoredMessage[];
}

export const chatApi = {
  async health(): Promise<ChatHealth> {
    return apiClient.get<ChatHealth>('/api/chat/health');
  },

  async retrieveContext(params: {
    ontologyId: string;
    query: string;
    seedLimit?: number;
    threshold?: number;
  }): Promise<GraphRagContext> {
    return apiClient.post<GraphRagContext>('/api/chat/retrieve', params);
  },

  async backfillEmbeddings(ontologyId: string): Promise<{
    embedded: number;
    ontologyId: string;
  }> {
    return apiClient.post(`/api/chat/backfill/${ontologyId}`);
  },

  /**
   * Lightweight check whether every node in the saved ontology has an
   * embedding row yet. Used by the chat header status icon.
   */
  async indexStatus(ontologyId: string): Promise<IndexStatus> {
    return apiClient.get<IndexStatus>(`/api/chat/index-status/${ontologyId}`);
  },

  /**
   * Proxy a chat completion through the backend.
   * Avoids CORS issues with external AI provider APIs by letting the
   * server make the call using the user's stored (encrypted) API key.
   */
  async completion(params: ChatCompletionRequest): Promise<ChatCompletionResponse> {
    return apiClient.post<ChatCompletionResponse>('/api/chat/completion', params);
  },

  /**
   * Ask the Co-Ontologist to propose graph mutations (add node, add relation,
   * edit node). The backend calls the LLM with tool definitions and returns
   * both a text reply and structured GraphProposal objects.
   *
   * Uses the user's 'small' model role (falls back to 'medium' then primary).
   * Deletions are never proposed — they remain manual-only.
   */
  async propose(params: ProposeRequest): Promise<ProposeResponse> {
    return apiClient.post<ProposeResponse>('/api/chat/propose', params);
  },

  // ── Chat History — conversation persistence ────────────────────────────────

  /** List conversations for one ontology, most-recent first. */
  listConversations(ontologyId: string, options?: { includeArchived?: boolean }) {
    return apiClient.get<ChatConversationSummary[]>('/api/chat/conversations', {
      params: { ontologyId, ...(options?.includeArchived ? { archived: 'true' } : {}) },
    });
  },

  /** Create a new empty conversation for one ontology. */
  createConversation(ontologyId: string, title?: string) {
    return apiClient.post<ChatConversation>('/api/chat/conversations', { ontologyId, title });
  },

  /** Fetch a single conversation with its full message thread. */
  getConversation(conversationId: string) {
    return apiClient.get<ChatConversation>(`/api/chat/conversations/${conversationId}`);
  },

  /** Patch one or more fields. `messages` overwrites the full thread. */
  updateConversation(
    conversationId: string,
    patch: { messages?: StoredMessage[]; title?: string; isArchived?: boolean }
  ) {
    return apiClient.put<ChatConversation>(`/api/chat/conversations/${conversationId}`, patch);
  },

  /** Hard-delete a conversation (irreversible). */
  deleteConversation(conversationId: string) {
    return apiClient.delete<void>(`/api/chat/conversations/${conversationId}`);
  },
};

// Made with Bob
